import { collection, getDocs, updateDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Backfill organizerId for existing events
 * 
 * Rules:
 * - If event has NO organizerId BUT has createdBy → set organizerId = createdBy
 * - Do NOT overwrite existing organizerId
 * - Process in batches for better performance
 */
export const backfillOrganizerId = async () => {
  console.log('🔄 Starting organizerId backfill...');
  
  try {
    // Fetch all events
    const eventsSnapshot = await getDocs(collection(db, 'events'));
    const totalEvents = eventsSnapshot.size;
    console.log(`📊 Total events found: ${totalEvents}`);
    
    let updatedCount = 0;
    let skippedCount = 0;
    let errorCount = 0;
    
    // Process events in batches of 500 (Firestore batch limit)
    const batchSize = 500;
    let batch = writeBatch(db);
    let batchCount = 0;
    
    for (const eventDoc of eventsSnapshot.docs) {
      const eventData = eventDoc.data();
      const eventId = eventDoc.id;
      
      // Check if backfill is needed
      const needsBackfill = !eventData.organizerId && eventData.createdBy;
      
      if (needsBackfill) {
        try {
          // Add to batch
          const eventRef = doc(db, 'events', eventId);
          batch.update(eventRef, {
            organizerId: eventData.createdBy
          });
          
          batchCount++;
          updatedCount++;
          
          console.log(`✅ Event ${eventId}: organizerId set to ${eventData.createdBy}`);
          
          // Commit batch if limit reached
          if (batchCount >= batchSize) {
            await batch.commit();
            console.log(`💾 Batch committed (${batchCount} updates)`);
            batch = writeBatch(db);
            batchCount = 0;
          }
        } catch (error) {
          console.error(`❌ Error updating event ${eventId}:`, error);
          errorCount++;
        }
      } else if (eventData.organizerId) {
        console.log(`⏭️ Event ${eventId}: already has organizerId, skipping`);
        skippedCount++;
      } else if (!eventData.createdBy) {
        console.log(`⚠️ Event ${eventId}: no createdBy field, cannot backfill`);
        skippedCount++;
      }
    }
    
    // Commit remaining batch
    if (batchCount > 0) {
      await batch.commit();
      console.log(`💾 Final batch committed (${batchCount} updates)`);
    }
    
    // Summary
    console.log('\n📊 Backfill Summary:');
    console.log(`   Total events: ${totalEvents}`);
    console.log(`   ✅ Updated: ${updatedCount}`);
    console.log(`   ⏭️ Skipped: ${skippedCount}`);
    console.log(`   ❌ Errors: ${errorCount}`);
    console.log('\n✨ Backfill complete!');
    
    return {
      success: true,
      totalEvents,
      updatedCount,
      skippedCount,
      errorCount
    };
    
  } catch (error) {
    console.error('❌ Backfill failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check how many events need backfill (dry run)
 */
export const checkBackfillNeeded = async () => {
  console.log('🔍 Checking events that need backfill...');
  
  try {
    const eventsSnapshot = await getDocs(collection(db, 'events'));
    const totalEvents = eventsSnapshot.size;
    
    let needsBackfill = 0;
    let hasOrganizerId = 0;
    let noCreatedBy = 0;
    
    eventsSnapshot.docs.forEach(eventDoc => {
      const eventData = eventDoc.data();
      
      if (!eventData.organizerId && eventData.createdBy) {
        needsBackfill++;
      } else if (eventData.organizerId) {
        hasOrganizerId++;
      } else if (!eventData.createdBy) {
        noCreatedBy++;
      }
    });
    
    console.log('\n📊 Backfill Analysis:');
    console.log(`   Total events: ${totalEvents}`);
    console.log(`   ✅ Already have organizerId: ${hasOrganizerId}`);
    console.log(`   🔄 Need backfill: ${needsBackfill}`);
    console.log(`   ⚠️ Missing createdBy: ${noCreatedBy}`);
    
    return {
      totalEvents,
      needsBackfill,
      hasOrganizerId,
      noCreatedBy
    };
    
  } catch (error) {
    console.error('❌ Check failed:', error);
    return { error: error.message };
  }
};

